import { Component, EventEmitter, Output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';
import { RippleModule } from 'primeng/ripple';
import { AuthService } from '../../core/services/auth.service';
import { PermissionService } from '../../core/services/permission.service';
import { PERMISSIONS } from '../../core/constants/permissions.constants';

export interface SidebarMenuItem {
  label:                string;
  icon:                 string;
  route:                string;
  activePrefix?:        string;
  requiredPermission?:  string;
  /** Visible si el usuario tiene AL MENOS uno de estos permisos. */
  requiredPermissions?: string[];
  /** `'admin'` → solo cuando el superadmin NO está en una empresa. `'workspace'` → resto de casos. */
  mode:                 'admin' | 'workspace';
}

const ALL_MENU_ITEMS: SidebarMenuItem[] = [
  { label: 'Inicio',               icon: 'pi pi-home',              route: '/app/dashboard',          mode: 'workspace' },
  { label: 'Importaciones',        icon: 'pi pi-arrow-circle-down', route: '/app/imports',            mode: 'workspace', requiredPermission: PERMISSIONS.IMPORTS_ACCESS },
  { label: 'Exportaciones',        icon: 'pi pi-box',               route: '/app/exports',            mode: 'workspace', requiredPermission: PERMISSIONS.EXPORTS_ACCESS },
  { label: 'Chat',                 icon: 'pi pi-comments',          route: '/app/chat',               mode: 'workspace', requiredPermissions: [PERMISSIONS.CHAT_USE, PERMISSIONS.CHAT_VIEW] },
  { label: 'Empresas',             icon: 'pi pi-building',          route: '/app/admin/company/list', mode: 'admin',     requiredPermission: PERMISSIONS.TENANTS_VIEW, activePrefix: '/app/admin/company' },
];

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    TooltipModule,
    DividerModule,
    RippleModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  private readonly authService  = inject(AuthService);
  private readonly router       = inject(Router);
  private readonly permService  = inject(PermissionService);

  @Output() collapsedChange = new EventEmitter<boolean>();

  collapsed = true;

  readonly visibleMenuItems = computed<SidebarMenuItem[]>(() => {
    const workspaceMode = this.permService.isInWorkspaceMode();
    return ALL_MENU_ITEMS.filter(item => {
      if (item.mode === 'admin'     &&  workspaceMode) return false;
      if (item.mode === 'workspace' && !workspaceMode) return false;
      return item.requiredPermission  ? this.permService.has(item.requiredPermission)       :
             item.requiredPermissions ? this.permService.hasAny(item.requiredPermissions) :
             true;
    });
  });

  isItemActive(item: SidebarMenuItem): boolean {
    const prefix = item.activePrefix ?? item.route;
    return this.router.isActive(prefix, {
      paths:       'subset',
      queryParams: 'ignored',
      fragment:    'ignored',
      matrixParams:'ignored',
    });
  }

  toggleSidebar(): void {
    this.collapsed = !this.collapsed;
    this.collapsedChange.emit(this.collapsed);
  }

  logout(): void {
    this.authService.logout();
  }
}
